// ============================================
// APPLICATION - Prospecto Premium Hook
// ============================================
// Anuncio de Premium para almacenes que venden y aún no pagan. Pensado para no pesar:
//  - nunca corre en el login: espera ESPERA_MS con la persona ya dentro de la app;
//  - como mucho una consulta por día y por dispositivo (y el backend además cachea el veredicto por día);
//  - solo para propietarios sin Premium.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { suscripcionRepository } from '../../infrastructure/http/suscripcion.repository';
import { prospectoPremiumStorage } from '../../infrastructure/storage/prospecto-premium.storage';
import { queryKeys } from '../query-keys';
import { useAuthStore } from '../../state/auth.store';
import { getFechaHoyChile } from '../../shared/date-utils';

const ESPERA_MS = 30_000;

export const useProspectoPremium = () => {
  const { session, accessToken } = useAuthStore();
  const queryClient = useQueryClient();
  const [esperaCumplida, setEsperaCumplida] = useState(false);

  const almacenId = session?.almacen?.id ?? null;
  // No se filtra por hasPremiumAccess: una prueba por vencer también cuenta como acceso Premium y el Backend decide
  // si ofrecerla (el veredicto de quien ya paga es barato y se cachea por día).
  const aplica = !!(accessToken && session && almacenId && session.user.esPropietario);

  useEffect(() => {
    if (!aplica) return;
    const timer = setTimeout(() => setEsperaCumplida(true), ESPERA_MS);
    return () => clearTimeout(timer);
  }, [aplica]);

  const hoy = getFechaHoyChile();
  const habilitado = aplica && esperaCumplida && !prospectoPremiumStorage.yaConsultadoHoy(almacenId as number, hoy);

  const { data } = useQuery({
    queryKey: queryKeys.suscripcion.prospecto(),
    queryFn: async () => {
      const resultado = await suscripcionRepository.getProspecto();
      prospectoPremiumStorage.marcarConsultado(almacenId as number, hoy);
      return resultado;
    },
    enabled: habilitado,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const descartar = useMutation({
    mutationFn: () => suscripcionRepository.descartarProspecto(),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.suscripcion.prospecto(), (prev: typeof data) =>
        prev ? { ...prev, mostrarAnuncio: false } : prev,
      );
    },
  });

  // Pulsó "hablar con ventas": queda como interesado (lead) para que ventas haga seguimiento.
  const marcarInteresado = useMutation({
    mutationFn: () => suscripcionRepository.marcarInteresProspecto(),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.suscripcion.prospecto(), (prev: typeof data) =>
        prev ? { ...prev, mostrarAnuncio: false } : prev,
      );
    },
  });

  // Candado síncrono: un doble clic (o "No, gracias" y "Hablar con ventas" casi a la vez) no debe enviar dos
  // peticiones. Cada "no" alarga la espera del anuncio (15, 30, 45 días), así que contarlo dos veces no es inocuo.
  // Se libera al terminar la petición, bien o mal, para poder reintentar si falló.
  const enviando = useRef(false);
  const enviar = (ejecutar: (alTerminar: () => void) => void): boolean => {
    if (enviando.current) return false;
    enviando.current = true;
    ejecutar(() => {
      enviando.current = false;
    });
    return true;
  };

  return {
    mostrarAnuncio: !!(data?.esProspecto && data.mostrarAnuncio),
    rutAlmacen: data?.rutAlmacen ?? null,
    /** true mientras hay una petición en curso (para deshabilitar los botones). */
    procesando: descartar.isPending || marcarInteresado.isPending,
    /** Devuelven false si ya había una petición en curso y esta se ignoró. */
    descartar: () => enviar((alTerminar) => descartar.mutate(undefined, { onSettled: alTerminar })),
    marcarInteresado: () =>
      enviar((alTerminar) => marcarInteresado.mutate(undefined, { onSettled: alTerminar })),
  };
};
